import BaseService from '../../base/base_service';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import AnonymousRestrictionsStrategy from './strategies/anonymous_restrictions_strategy';
import AuthorizedRestrictionsStrategy from './strategies/authorized_restrictions_strategy';
import Device from '../../utils/device/device';
import IRestrictions, { RestrictionsServiceInitParams } from './restrictions_service_interface';


type CurrentStrategy = AnonymousRestrictionsStrategy | AuthorizedRestrictionsStrategy;

export default class RestrictionsService extends BaseService implements IRestrictions {

  static CLASS: string = 'RestrictionsService';
  private _initParams: RestrictionsServiceInitParams = null;
  private _anonymousRestrictionsStrategy: AnonymousRestrictionsStrategy = null;
  private _authorizedRestrictionStrategy: AuthorizedRestrictionsStrategy = null;
  private _currentRestrictionsStrategy: CurrentStrategy = null;

  init(params: RestrictionsServiceInitParams) {
    super.init(params);
    this._setInitParams(params);

    // Anonymous restrictions are always the same and they do not change after the application loaded.
    // So that's why we cache them.
    this._anonymousRestrictionsStrategy = AnonymousRestrictionsStrategy.create(Object.assign(this._getCommonStrategyParams(), params));
    this._subscribe();
  }

  private _setInitParams(params: RestrictionsServiceInitParams): void {
    this._initParams = {
      hybridPermittedMap: params.hybridPermittedMap ? params.hybridPermittedMap : {}
    };
  }

  private _getCommonStrategyParams() {
    return   {
      getServiceFactoryCallback: () => this.serviceFactory
    };
  }

  get currentRestrictionsStrategy() {
    if(!this._currentRestrictionsStrategy) {
      this._switchStrategy(false);
    }

    return this._currentRestrictionsStrategy;
  }

  private _switchStrategy(fireEvent: boolean = true): void {
    //this._currentRestrictionsStrategy.dispose();
    if(this.serviceFactory.oAuth2Service().isAuthorized()) {
      //if we are already in the authorized strategy then we dont't need to recreate it and fire the event
      if(!(this._currentRestrictionsStrategy instanceof  AuthorizedRestrictionsStrategy)) {
        if (!this._authorizedRestrictionStrategy) {
          this._authorizedRestrictionStrategy = AuthorizedRestrictionsStrategy.create(this._getCommonStrategyParams());;
        }
        this._currentRestrictionsStrategy = this._authorizedRestrictionStrategy;
        if(fireEvent) {
          Event.fire(SYSTEM_EVENTS.RESTRICTIONS_CHANGED);
        }
      }

    } else {
      //if we are already in the anonymous strategy then we dont't to fire event
      if(!(this._currentRestrictionsStrategy instanceof  AnonymousRestrictionsStrategy)) {
        this._currentRestrictionsStrategy = this._anonymousRestrictionsStrategy;
        if(fireEvent) {
          Event.fire(SYSTEM_EVENTS.RESTRICTIONS_CHANGED);
        }
      }
    }
  }

  private _onOauthStateChanged(): void {
    this._switchStrategy();
  }

  public getGameRestriction(gameType: number, gameGroupId: number, vendorId: number): any {
      return this.currentRestrictionsStrategy.getGameRestriction(gameType, gameGroupId, vendorId);
  }

  public doAsyncJobs(): Promise<any> {
    return this._anonymousRestrictionsStrategy.doAsyncJobs()
      .then(() => this.showClientRestrictionMessage());
  }

  public showClientRestrictionMessage(): void {

    if (this._anonymousRestrictionsStrategy.isApplicationRestricted()) {
      const translation = this.serviceFactory.translationProxyService();
      const titleText = translation.translate('RESTRICTION_FOR_USE_TITLE');
      const restrictionText = translation.translate('RESTRICTION_FOR_USE_TEXT');

      this.serviceFactory.dialogProxyService().showSimpleTextModal({
        title: titleText,
        text: restrictionText,
        hideCloseButton: true,
        disableFullscreenMobile: false
      });

    }
  }

  private _subscribe(): void {
    this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOauthStateChanged);
    if(Device.hybrid.inCasino && Device.os.isIOS) {
      this.onSystemEvent(SYSTEM_EVENTS.ADD_TAG_OPEN_APP_VER, this._onHybridInfoComplete);
    }
  }

  //show country restriction message to user that use app that not permitted in his country.
  private _onHybridInfoComplete(): void {
    const country = this.serviceFactory.countryService().getCountry();
    const appID = this.serviceFactory.hybridService().getAppleID();
    const permittedMap = this._initParams.hybridPermittedMap;
    const permittedCountries = permittedMap[appID] || permittedMap.DEFAULT;
    this.logger.info('[RestrictionService] permittedCountries:' + permittedCountries + 'current country:' + country);

    if(permittedCountries !== 'All' && permittedCountries !== country) {
      this.logger.info('[RestrictionService] Hybrid restricted for user country, country:' + country);

      this._openRelevantRestrictionMessage(permittedMap.messageType, country);
    }
  }

  //Open relevant restriction message according to message type.
  private _openRelevantRestrictionMessage(messageType: string, country: string): void {
    this._sendRestrictionAnalytics('hybrid_restriction_country', 'restricted_country', country);

    switch (messageType) {
      case '1':
        this._openCountryRestrictionMessage();
        break;
      default:
        this._openCountryRestrictionMessage();
        break;
    }
  }

 //Open country restriction message.
  private _openCountryRestrictionMessage(): void {
    const translation = this.serviceFactory.translationProxyService();
    const titleText = translation.translate('RESTRICTION_FOR_USE_TITLE');
    const restrictionText = translation.translate('RESTRICTION_FOR_USE_TEXT');

    this.serviceFactory.dialogProxyService().showSimpleTextModal({
      title: titleText,
      text: restrictionText,
      hideCloseButton: true,
      disableFullscreenMobile: false
    });
  }


  private _sendRestrictionAnalytics(action: string, label: string, value: string): void {
    const analyticsData = {
      category: 'Restrictions',
      action: action,
      label: label,
      value: value
    };

    Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticsData)
  }

}